import { SURAHS } from '../surahs'
import { getAudioUrl } from '../reciters'
import { playlistsApi } from '../api'
import type { Ayah } from '../../types'

/**
 * Offline-first surah download manager.
 *
 * Stores explicitly-downloaded content in dedicated Cache Storage namespaces
 * so the service worker's runtime caches (which LRU-evict for incidental
 * browsing) don't reclaim them. Maintains a localStorage manifest of what
 * the user has chosen to keep offline.
 *
 * Why two caches? `qlearn-offline-quran-v1` and `qlearn-offline-audio-v1`
 * mirror the SW's runtime split, which keeps quota accounting cleaner —
 * audio dwarfs text 100×+ and we may want to clear one without the other.
 *
 * The SW already serves `/quran/*` and `/audio/*` from its own caches via
 * `CacheFirst`. When the user is offline, the SW will fall back to its
 * cache and find the assets we've put there (workbox checks every cache
 * matching the path through cacheName-keyed lookups via plugin chains).
 * To make this bullet-proof we ALSO register these cache names in the
 * SW config so workbox's match() walks them.
 */

// Exported so Task 6's text-pack module (and tests) can reference the exact
// cache namespace names without re-deriving the string literal — the names
// themselves are unchanged, only now part of this module's public surface.
export const OFFLINE_QURAN_CACHE_NAME = 'qlearn-offline-quran-v1'
export const OFFLINE_AUDIO_CACHE_NAME = 'qlearn-offline-audio-v1'
const CACHE_QURAN = OFFLINE_QURAN_CACHE_NAME
const CACHE_AUDIO = OFFLINE_AUDIO_CACHE_NAME
// Long-TTL cache for offline-pinned API responses (currently /playlists* —
// see vite.config.ts rule 3b). Written into by the SW's CacheFirst handler
// during the explicit pre-fetch at download time.
const CACHE_API_OFFLINE = 'qlearn-api-offline-v1'
const MANIFEST_KEY = 'qlearn-offline-manifest-v1'

interface OfflineManifest {
  // Schema version. Bumped to 2 for per-surah `bytes` accounting and the
  // `textPack` block below; readManifest() migrates a legacy (unversioned,
  // v1) manifest in place, so the localStorage key itself never changes.
  v: 2
  // Per-surah ISO timestamp of when it was downloaded. Allows showing
  // "Downloaded 3 days ago" and tracking what's actually stored.
  // `bytes` is the audio payload size recorded at download time (text +
  // translation bytes are deliberately excluded — see downloadSurah).
  // `null` means "not yet known" — either a legacy v1 entry pending
  // backfillSurahBytes(), or a best-effort write that couldn't measure.
  surahs: Record<number, { downloadedAt: string; reciter: string; edition: string; bytes: number | null }>
  // Playlists keyed by playlist id. Stores the surah numbers that were
  // part of the download so "remove playlist" only nukes the ones not
  // shared with other downloaded playlists.
  playlists: Record<string, { downloadedAt: string; surahNumbers: number[] }>
  // Text-pack completion markers (whole-Qur'an text + per-edition
  // translations). Kept out of the surah/byte accounting on purpose: text
  // is a flat, small download independent of which surahs/reciters are
  // pinned, so it has its own completion state rather than a byte budget.
  textPack: {
    text: { completedAt: string } | null
    translations: Record<string, { completedAt: string }>
  }
}

const EMPTY_MANIFEST: OfflineManifest = { v: 2, surahs: {}, playlists: {}, textPack: { text: null, translations: {} } }

/**
 * Read the manifest from localStorage, migrating a legacy (unversioned, v1)
 * shape in place: existing surah entries are kept as-is and gain
 * `bytes: null` (unknown — pending backfillSurahBytes()), and an empty
 * `textPack` is synthesized. The migrated shape isn't written back by this
 * function — it's persisted the next time something calls writeManifest()
 * (a new download, a text-pack marker, or backfillSurahBytes() itself).
 */
function readManifest(): OfflineManifest {
  try {
    const raw = localStorage.getItem(MANIFEST_KEY)
    if (!raw) return structuredClone(EMPTY_MANIFEST)
    const parsed = JSON.parse(raw)
    const surahs: OfflineManifest['surahs'] = {}
    for (const [k, v] of Object.entries(parsed.surahs ?? {})) {
      const entry = v as { downloadedAt: string; reciter: string; edition: string; bytes?: number | null }
      surahs[Number(k)] = { ...entry, bytes: typeof entry.bytes === 'number' ? entry.bytes : null }
    }
    return {
      v: 2,
      surahs,
      playlists: parsed.playlists ?? {},
      textPack: {
        text: parsed.textPack?.text ?? null,
        translations: parsed.textPack?.translations ?? {},
      },
    }
  } catch {
    return structuredClone(EMPTY_MANIFEST)
  }
}

function writeManifest(m: OfflineManifest): void {
  try {
    localStorage.setItem(MANIFEST_KEY, JSON.stringify(m))
  } catch {
    // localStorage can throw in private mode / over-quota — manifest writes
    // become best-effort; the user just loses memory of what's downloaded
    // (the cache itself stays intact).
  }
}

// ─── URL builders ────────────────────────────────────────────────────────

function quranTextUrl(surahNumber: number): string {
  return `/quran/text/${surahNumber}.json`
}

function translationUrl(edition: string, surahNumber: number): string {
  return `/quran/translations/${edition}/${surahNumber}.json`
}

function audioUrls(reciter: string, surahNumber: number): string[] {
  const surah = SURAHS.find((s) => s.number === surahNumber)
  if (!surah) return []
  const urls: string[] = []
  for (let ayahNumber = 1; ayahNumber <= surah.numberOfAyahs; ayahNumber++) {
    urls.push(getAudioUrl(reciter, { surahNumber, ayahNumber } as Ayah))
  }
  return urls
}

// ─── Cache I/O ───────────────────────────────────────────────────────────

/**
 * Fetch + cache.put, reporting the number of bytes stored (or `null` on
 * failure) so callers can accumulate download size for the usage/budget
 * accounting in getUsageBytes()/wouldExceedBudget(). Content-Length is
 * trusted when present — cheaper than materializing a blob just to measure
 * it — and only when it's absent (some proxy/dev setups strip it) do we
 * clone the response and measure the actual blob.
 */
async function putInCache(
  cacheName: string,
  url: string,
  signal?: AbortSignal,
): Promise<number | null> {
  if (typeof caches === 'undefined') return null
  try {
    const cache = await caches.open(cacheName)
    // CRITICAL: `mode: 'cors'` so the cached response is NOT opaque.
    // Opaque responses (status 0) silently fail `response.arrayBuffer()`
    // — and that's exactly what `audioFetcher.ts` calls before passing
    // to Web Audio's decodeAudioData. The CDN already serves CORS headers
    // (the regular reader's fetch defaults to cors and works in prod), so
    // matching that mode here is what makes offline playback actually work.
    // `signal` wires user-initiated cancel into in-flight HTTP requests
    // so cancel stops byte-transfer immediately rather than only between
    // batches.
    const response = await fetch(url, { mode: 'cors', cache: 'reload', signal })
    if (!response.ok) return null
    const lenHeader = response.headers.get('content-length')
    const headerBytes = lenHeader !== null ? Number(lenHeader) : NaN
    if (Number.isFinite(headerBytes) && headerBytes >= 0) {
      await cache.put(url, response.clone())
      return headerBytes
    }
    // No usable Content-Length — clone BEFORE cache.put() consumes the
    // body, then materialize that clone as a blob purely to size it.
    const sizeProbe = response.clone()
    await cache.put(url, response.clone())
    return (await sizeProbe.blob()).size
  } catch {
    return null
  }
}

async function deleteFromCaches(url: string): Promise<void> {
  if (typeof caches === 'undefined') return
  const names = [CACHE_QURAN, CACHE_AUDIO]
  await Promise.all(
    names.map(async (n) => {
      try {
        const cache = await caches.open(n)
        await cache.delete(url)
      } catch {
        /* ignore */
      }
    }),
  )
}

// ─── Public API ──────────────────────────────────────────────────────────

export interface DownloadProgress {
  total: number
  completed: number
  failed: number
  cancelled: boolean
}

export type ProgressCallback = (p: DownloadProgress) => void

export interface DownloadSurahOptions {
  reciter: string
  edition: string
  onProgress?: ProgressCallback
  signal?: AbortSignal
}

/**
 * Download a single surah's text + translation + audio for the given reciter.
 * Audio fetches happen with concurrency 4 to stay within reasonable network
 * pressure on mobile.
 */
export async function downloadSurah(
  surahNumber: number,
  options: DownloadSurahOptions,
): Promise<DownloadProgress> {
  const { reciter, edition, onProgress, signal } = options

  const textUrl = quranTextUrl(surahNumber)
  const trUrl = translationUrl(edition, surahNumber)
  const audUrls = audioUrls(reciter, surahNumber)

  const total = 2 + audUrls.length
  let completed = 0
  let failed = 0
  const report = () => onProgress?.({ total, completed, failed, cancelled: false })
  report()

  // Text + translation first (small, fast) so the user gets text-offline
  // value even if audio download is interrupted mid-way. Their bytes are
  // NOT added to `audioBytes` below — only audio counts toward the
  // manifest's `bytes`/usage accounting (text-pack has its own markers).
  for (const url of [textUrl, trUrl]) {
    if (signal?.aborted) {
      return { total, completed, failed, cancelled: true }
    }
    const bytes = await putInCache(CACHE_QURAN, url, signal)
    if (bytes !== null) completed++
    else failed++
    report()
  }

  // Audio in parallel batches. Window size 4 keeps memory + network sane
  // and is friendly to mobile data plans.
  const CONCURRENCY = 4
  let cursor = 0
  let cancelled = false
  let audioBytes = 0
  while (cursor < audUrls.length) {
    if (signal?.aborted) {
      cancelled = true
      break
    }
    const slice = audUrls.slice(cursor, cursor + CONCURRENCY)
    const results = await Promise.all(slice.map((u) => putInCache(CACHE_AUDIO, u, signal)))
    for (const b of results) {
      if (b !== null) {
        completed++
        audioBytes += b
      } else {
        failed++
      }
    }
    report()
    cursor += CONCURRENCY
  }
  // Final abort check — the signal may have fired between the loop
  // condition and a Promise.all that already raced ahead.
  if (signal?.aborted) cancelled = true

  // Pin the Bismillah preamble (Surah 1, Ayah 1) that playlist playback
  // prepends before every surah ≠ 1 when `bismillahBeforeSurah` is on.
  // Without it, a "downloaded" surah's offline playback stalls at the
  // preamble once the SW's runtime audio cache evicts 001001.mp3. Probe
  // first so playlist downloads don't re-fetch it once per surah.
  // Best-effort and NOT counted in `total`; deliberately NOT added to
  // audioUrls() — that list also drives removeSurah, and removing one
  // surah must not delete the preamble shared by the others. The pinned
  // file is only reclaimed by removeAll.
  if (!cancelled && surahNumber !== 1 && typeof caches !== 'undefined') {
    try {
      const bisUrl = getAudioUrl(reciter, { surahNumber: 1, ayahNumber: 1 } as Ayah)
      const cache = await caches.open(CACHE_AUDIO)
      if (!(await cache.match(bisUrl, { ignoreVary: true }))) {
        await putInCache(CACHE_AUDIO, bisUrl, signal)
      }
    } catch {
      /* best-effort */
    }
  }

  // Persist manifest only when the download was NOT cancelled. Writing a
  // partial entry on cancel would have the UI claim "Available offline"
  // for a surah whose audio is half-fetched and would fail to play.
  if (!cancelled && completed > 0) {
    const manifest = readManifest()
    manifest.surahs[surahNumber] = {
      downloadedAt: new Date().toISOString(),
      reciter,
      edition,
      bytes: audioBytes,
    }
    writeManifest(manifest)
  }

  return { total, completed, failed, cancelled }
}

/** Remove a surah's cached text + audio + manifest entry. */
export async function removeSurah(surahNumber: number, options?: { keepIfInPlaylist?: boolean }): Promise<void> {
  const manifest = readManifest()
  const stored = manifest.surahs[surahNumber]
  if (!stored) return

  // Skip removal if requested and the surah is part of another playlist
  // download. Prevents "remove playlist X" from breaking playlist Y that
  // shares some surahs.
  if (options?.keepIfInPlaylist) {
    const inAnotherPlaylist = Object.values(manifest.playlists).some((p) =>
      p.surahNumbers.includes(surahNumber),
    )
    if (inAnotherPlaylist) return
  }

  // The background text pack (see textPack.ts / getTextPackState below) pins
  // the whole Qur'an's Arabic text and, per edition, every translation as an
  // always-offline baseline independent of which surahs are explicitly
  // downloaded. Once a marker is set, ensureTextPack() early-returns and
  // never re-pins that file — so removing a surah must not delete a
  // text/translation file the text pack already owns, or it punches a
  // permanent hole in that baseline. Audio has no such baseline and is
  // always removed. removeAll() bypasses this — it wipes the markers
  // together with the caches, so there's nothing to preserve there.
  const textPackState = getTextPackState()
  const urls: string[] = [...audioUrls(stored.reciter, surahNumber)]
  if (textPackState.textCompletedAt === null) {
    urls.push(quranTextUrl(surahNumber))
  }
  if (!textPackState.translations[stored.edition]) {
    urls.push(translationUrl(stored.edition, surahNumber))
  }
  await Promise.all(urls.map(deleteFromCaches))
  delete manifest.surahs[surahNumber]
  writeManifest(manifest)
}

export interface DownloadPlaylistOptions extends DownloadSurahOptions {
  playlistId: string
  surahNumbers: number[]
  /**
   * Per-surah reciter override (surah number → reciter identifier), sourced
   * from the playlist's per-row settings. A surah absent from this map (or
   * the map itself being absent) downloads with the playlist-wide `reciter`.
   * The manifest always records the EFFECTIVE reciter that was actually
   * fetched, so a later `removeSurah`/offline-playback lookup agrees with
   * what's really in the cache.
   */
  reciterBySurah?: Record<number, string>
}

/** Download every surah in a playlist. Progress is cumulative across surahs. */
export async function downloadPlaylist(options: DownloadPlaylistOptions): Promise<DownloadProgress> {
  const { playlistId, surahNumbers, onProgress, signal, reciterBySurah, ...surahOpts } = options

  // Dedup — playlists can contain the same surah twice (rare but possible);
  // we'd otherwise fetch and account for it twice. Preserves order.
  const uniqueSurahs = Array.from(new Set(surahNumbers))

  // Pre-compute total work so the progress bar moves predictably across surahs.
  let total = 0
  for (const n of uniqueSurahs) {
    const s = SURAHS.find((x) => x.number === n)
    if (s) total += 2 + s.numberOfAyahs
  }
  let completed = 0
  let failed = 0
  let cancelled = false
  const report = () => onProgress?.({ total, completed, failed, cancelled })

  // Aggregate INNER progress into the OUTER counter so the bar moves
  // smoothly inside each surah (not just at surah boundaries). We track a
  // baseline `priorCompleted` per surah so the inner counts add correctly.
  let priorCompleted = 0
  let priorFailed = 0

  report()
  for (const n of uniqueSurahs) {
    if (signal?.aborted) {
      cancelled = true
      break
    }
    const result = await downloadSurah(n, {
      ...surahOpts,
      reciter: reciterBySurah?.[n] || surahOpts.reciter,
      onProgress: (p) => {
        completed = priorCompleted + p.completed
        failed = priorFailed + p.failed
        report()
      },
      signal,
    })
    priorCompleted += result.completed
    priorFailed += result.failed
    completed = priorCompleted
    failed = priorFailed
    if (result.cancelled) {
      cancelled = true
      break
    }
    report()
  }
  if (signal?.aborted) cancelled = true

  // Cache the playlist's backend metadata so it loads offline. Without
  // this the offline reader/playlist detail page would render an empty
  // error state because `playlistsApi.get(id)` would hit the network and
  // fail. Pinned to the long-TTL `qlearn-api-offline-v1` cache via the
  // SW runtime rule keyed on `/playlists*`.
  // Best-effort — a failure here doesn't invalidate the offline content
  // download. Wrapped in try/catch so the user still gets their audio
  // even if the API is briefly unreachable at download time.
  if (!cancelled) {
    try {
      await Promise.all([
        playlistsApi.get(playlistId), // detail
        playlistsApi.list(), // index — so /playlists list works offline too
      ])
    } catch {
      // Surfaced separately via the SW cache's age — if the metadata isn't
      // cached, the user's offline view will fall back to its empty state.
    }
  }

  // Skip manifest write on cancel — same reason as downloadSurah: don't
  // claim "Available offline" for a playlist whose audio is half-fetched.
  if (!cancelled && completed > 0) {
    const manifest = readManifest()
    manifest.playlists[playlistId] = {
      downloadedAt: new Date().toISOString(),
      surahNumbers: uniqueSurahs,
    }
    writeManifest(manifest)
  }

  return { total, completed, failed, cancelled }
}

/** Remove a playlist's cached content. Surahs shared with other downloaded
 *  playlists or with a standalone per-surah download are KEPT. */
export async function removePlaylist(playlistId: string): Promise<void> {
  const manifest = readManifest()
  const stored = manifest.playlists[playlistId]
  if (!stored) return

  // Build the set of surahs we can safely remove (not standalone-downloaded,
  // not part of any OTHER playlist).
  const otherPlaylists = Object.entries(manifest.playlists).filter(
    ([id]) => id !== playlistId,
  )
  for (const surahNumber of stored.surahNumbers) {
    const inStandalone = manifest.surahs[surahNumber] !== undefined
    const inOtherPlaylist = otherPlaylists.some(([, p]) =>
      p.surahNumbers.includes(surahNumber),
    )
    if (!inStandalone && !inOtherPlaylist) {
      await removeSurah(surahNumber)
    }
  }
  delete manifest.playlists[playlistId]
  writeManifest(manifest)
}

/** Wipe all offline caches and the manifest. */
export async function removeAll(): Promise<void> {
  if (typeof caches !== 'undefined') {
    await Promise.all([
      caches.delete(CACHE_QURAN),
      caches.delete(CACHE_AUDIO),
      caches.delete(CACHE_API_OFFLINE),
    ])
  }
  writeManifest(structuredClone(EMPTY_MANIFEST))
}

export function isSurahDownloaded(surahNumber: number): boolean {
  return readManifest().surahs[surahNumber] !== undefined
}

export function isPlaylistDownloaded(playlistId: string): boolean {
  return readManifest().playlists[playlistId] !== undefined
}

export function getManifest(): OfflineManifest {
  return readManifest()
}

// ─── Usage, budget, and text-pack accounting ────────────────────────────

/** Sum of every downloaded surah's recorded audio bytes. Legacy entries
 *  with `bytes: null` (not yet backfilled) are excluded rather than
 *  treated as 0 — undercounting a stale manifest is safer than a budget
 *  check that lets a user blow past their cap because of missing data. */
export function getUsageBytes(): number {
  const m = readManifest()
  let sum = 0
  for (const entry of Object.values(m.surahs)) {
    if (typeof entry.bytes === 'number') sum += entry.bytes
  }
  return sum
}

/** budgetMB === 0 means "no limit". */
export function wouldExceedBudget(estimateBytes: number, budgetMB: number): boolean {
  if (budgetMB <= 0) return false
  return getUsageBytes() + estimateBytes > budgetMB * 1024 * 1024
}

/** One-shot: compute bytes for legacy (v1) surah entries from the cache.
 *  Best-effort — a `caches` failure here just leaves those entries at
 *  `bytes: null` to be retried on the next app boot, same as before. */
export async function backfillSurahBytes(): Promise<void> {
  if (typeof caches === 'undefined') return
  const manifest = readManifest()
  const pending = Object.entries(manifest.surahs).filter(([, e]) => e.bytes === null)
  if (pending.length === 0) return
  try {
    const cache = await caches.open(CACHE_AUDIO)
    for (const [numStr, entry] of pending) {
      const surahNumber = Number(numStr)
      let sum = 0
      for (const url of audioUrls(entry.reciter, surahNumber)) {
        const hit = await cache.match(url)
        if (hit) sum += (await hit.clone().blob()).size
      }
      entry.bytes = sum
    }
    writeManifest(manifest)
  } catch {
    // Best-effort; retried on the next app boot.
  }
}

/** Read-only view of text-pack completion, flattened to plain ISO strings
 *  for callers (Task 8's usage UI, Task 10's download gating). */
export function getTextPackState(): { textCompletedAt: string | null; translations: Record<string, string> } {
  const m = readManifest()
  const translations: Record<string, string> = {}
  for (const [ed, v] of Object.entries(m.textPack.translations)) translations[ed] = v.completedAt
  return { textCompletedAt: m.textPack.text?.completedAt ?? null, translations }
}

/** Mark the whole-Qur'an text pack as fully pinned. */
export function markTextPackText(): void {
  const m = readManifest()
  m.textPack.text = { completedAt: new Date().toISOString() }
  writeManifest(m)
}

/** Mark a single translation edition's text pack as fully pinned. */
export function markTextPackTranslation(edition: string): void {
  const m = readManifest()
  m.textPack.translations[edition] = { completedAt: new Date().toISOString() }
  writeManifest(m)
}

/** Pin a /quran/* JSON into the offline text cache (used by the text pack). */
export async function pinQuranFile(url: string, signal?: AbortSignal): Promise<boolean> {
  return (await putInCache(CACHE_QURAN, url, signal)) !== null
}

export interface StorageEstimate {
  usage: number // bytes
  quota: number // bytes (best effort)
}

/** Best-effort storage usage report. Falls back to {0,0} when the API is
 *  missing (older browsers). */
export async function estimateStorage(): Promise<StorageEstimate> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
    return { usage: 0, quota: 0 }
  }
  try {
    const est = await navigator.storage.estimate()
    return { usage: est.usage ?? 0, quota: est.quota ?? 0 }
  } catch {
    return { usage: 0, quota: 0 }
  }
}

/** Human-readable bytes formatter ("1.2 MB", "456 KB"). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
